import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateStatus, useSetReminder } from '../../hooks/useMarketing';
import OutreachModal from './OutreachModal';

const CLARITY_PROJECT_ID = 'wrbd6q0evd';

const COUNTRY_FLAGS = {
  AU: '🇦🇺', US: '🇺🇸', UK: '🇬🇧', GB: '🇬🇧', ES: '🇪🇸', DE: '🇩🇪',
  EU: '🇪🇺', NZ: '🇳🇿', CA: '🇨🇦', JP: '🇯🇵', CN: '🇨🇳', KR: '🇰🇷',
};

const STEP_LABELS = {
  paid: 'Paid',
  quote: 'Quote',
  checkout: 'Checkout',
  details: 'Details',
  countries: 'Countries',
  analysis: 'Analysis',
  business: 'Business',
  start: 'Start',
  landing: 'Landing',
};

const REPORT_BADGES = {
  generating: { label: '📄 Report generating', background: '#eff6ff', color: '#1d4ed8' },
  draft: { label: '📄 Report ready to review', background: '#fffbeb', color: '#b45309' },
  sent: { label: '📄 Report sent', background: '#ecfdf5', color: '#047857' },
  failed: { label: '📄 Report failed', background: '#fef2f2', color: '#dc2626' },
};

const REASON_LABELS = {
  price: 'Price',
  not_ready: 'Not ready',
  comparing: 'Comparing',
  question: 'Question',
  other: 'Other',
};

function VisitorCard({ visitor, selectMode, isSelected, onToggleSelect }) {
  const navigate = useNavigate();
  const [outreachOpen, setOutreachOpen] = useState(false);
  const updateStatus = useUpdateStatus();
  const setReminder = useSetReminder();

  const handleClick = () => {
    if (selectMode) {
      onToggleSelect?.(visitor.id);
      return;
    }
    navigate(`/marketing/visitor/${visitor.id}`);
  };

  // Engagement signals — visible at a glance so nobody gets double-asked
  const feedbackAskedAt = visitor.feedbackAskedAt
    || (visitor.outreachLog || []).filter((e) => e.template === 'wizard_feedback').map((e) => e.sentAt).sort().pop()
    || null;
  const hasOpenedEmail = !!visitor.emailOpenedAt
    || (visitor.outreachLog || []).some((e) => e.openedAt || e.clickedAt);

  // Next-email guidance is computed by the pipeline endpoint (single source
  // of truth in outreachStages.js) — null when this visitor shouldn't
  // receive outreach.
  const outreachStage = visitor.nextOutreach || null;
  const clarityId = (visitor.userId || '').replace(/^guest:/, '');
  const clarityUrl = `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/impressions?CustomUserId=is%3B${encodeURIComponent(clarityId)}&date=Last%2030%20days`;

  const timeAgo = getTimeAgo(visitor.updatedAt || visitor.createdAt);
  const source = formatSource(visitor.trafficSource, visitor.utmMedium);
  const stepLabel = STEP_LABELS[visitor.currentStep] || visitor.currentStep;

  return (
    <div
      className={`mkt-card ${selectMode ? 'mkt-card-selectable' : ''} ${isSelected ? 'mkt-card-selected' : ''}`}
      onClick={handleClick}
    >
      {selectMode && (
        <span className={`mkt-card-checkbox ${isSelected ? 'checked' : ''}`}>
          {isSelected ? '✓' : ''}
        </span>
      )}
      {/* Name + due + step badge */}
      <div className="mkt-card-identity">
        <span className="mkt-card-name">{visitor.name || visitor.email}</span>
        {visitor.followUpDue && (
          <span
            className="mkt-card-due-badge"
            style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '4px', fontSize: '10px', fontWeight: 700, padding: '1px 6px' }}
            title="Due for a follow-up"
          >
            🔔 DUE
          </span>
        )}
        <span className={`mkt-card-step-badge mkt-step-${visitor.currentStep}`} title="Furthest wizard step">
          {stepLabel}
        </span>
        {visitor.reportStatus && REPORT_BADGES[visitor.reportStatus] && (
          <span
            style={{
              background: REPORT_BADGES[visitor.reportStatus].background,
              color: REPORT_BADGES[visitor.reportStatus].color,
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 700,
              padding: '1px 6px',
              cursor: visitor.reportId ? 'pointer' : 'default',
            }}
            title={visitor.reportId ? 'Review report' : 'Report status'}
            onClick={(e) => {
              if (!visitor.reportId) return;
              e.stopPropagation();
              navigate(`/marketing/reports/${visitor.reportId}`);
            }}
          >
            {REPORT_BADGES[visitor.reportStatus].label}
          </span>
        )}
      </div>

      {/* Email + phone */}
      {visitor.email && (
        <div className="mkt-card-email">{visitor.email}</div>
      )}
      {visitor.phone && (
        <a
          href={`tel:${visitor.phone}`}
          className="mkt-card-phone"
          onClick={(e) => e.stopPropagation()}
          title="Call them"
        >
          📞 {visitor.phone}
        </a>
      )}

      {/* Trademark + business */}
      {visitor.trademarkName && (
        <div className="mkt-card-trademark">
          &ldquo;{visitor.trademarkName}&rdquo;
          {visitor.trademarkType === 'logo' && ' 🖼'}
        </div>
      )}
      {visitor.businessName && (
        <div className="mkt-card-business">{visitor.businessName}</div>
      )}
      {visitor.industry && (
        <div className="mkt-card-industry">{visitor.industry}</div>
      )}

      {/* Business description snippet */}
      {visitor.businessDescription && (
        <div className="mkt-card-description" title={visitor.businessDescription}>
          {visitor.businessDescription.length > 80
            ? visitor.businessDescription.substring(0, 80) + '...'
            : visitor.businessDescription}
        </div>
      )}

      {/* AI analysis summary snippet */}
      {visitor.analysisSummary && (
        <div className="mkt-card-analysis" title={visitor.analysisSummary}>
          <span className="mkt-card-analysis-label">AI:</span>{' '}
          {visitor.analysisSummary.length > 60
            ? visitor.analysisSummary.substring(0, 60) + '...'
            : visitor.analysisSummary}
        </div>
      )}

      {/* Countries + classes */}
      <div className="mkt-card-meta">
        {visitor.countries?.length > 0 && (
          <span className="mkt-card-countries">
            {visitor.countries.map((c) => COUNTRY_FLAGS[c?.toUpperCase?.()] || c).join(' ')}
          </span>
        )}
        {visitor.classCount > 0 && (
          <span className="mkt-card-classes">{visitor.classCount} class{visitor.classCount !== 1 ? 'es' : ''}</span>
        )}
      </div>

      {/* Cart value */}
      {visitor.cartValue > 0 && (
        <div className="mkt-card-value">${visitor.cartValue.toLocaleString()}</div>
      )}

      {/* Risk + returning + engagement badges */}
      <div className="mkt-card-badges">
        {visitor.riskLevel && (
          <span className={`mkt-card-risk mkt-risk-${visitor.riskLevel.toLowerCase()}`}>
            {visitor.riskLevel}
          </span>
        )}
        {visitor.isReturning && (
          <span className="mkt-badge mkt-badge-returning" title="Returning visitor">Returning</span>
        )}
        {hasOpenedEmail && (
          <span className="mkt-badge mkt-badge-opened" title={`Opened an email${visitor.lastEmailOpenedAt ? ` (${getTimeAgo(visitor.lastEmailOpenedAt)})` : ''}`}>
            ✉️ Opened
          </span>
        )}
        {visitor.feedbackReason && (
          <span className="mkt-badge mkt-badge-feedback-given" title="Gave feedback — see the Feedback tab">
            💬 {REASON_LABELS[visitor.feedbackReason] || visitor.feedbackReason}
          </span>
        )}
        {!visitor.feedbackReason && feedbackAskedAt && (
          <span className="mkt-badge mkt-badge-feedback-asked" title={`Feedback requested ${getTimeAgo(feedbackAskedAt)} — bulk send skips them for 30 days`}>
            💬 Asked {getTimeAgo(feedbackAskedAt)}
          </span>
        )}
      </div>

      {/* What their previous session looked like (returning visitors) */}
      {visitor.previousSessions?.length > 0 && (
        <div
          className="mkt-card-prev-visit"
          title={visitor.previousSessions
            .map((p) => `${getTimeAgo(p.visitedAt)}: reached ${STEP_LABELS[p.step] || p.step || '?'}${p.trademarkName ? ` — “${p.trademarkName}”` : ''}`)
            .join('\n')}
        >
          ↩ Last visit {getTimeAgo(visitor.previousSessions[0].lastActiveAt || visitor.previousSessions[0].visitedAt)}:
          {' '}reached {STEP_LABELS[visitor.previousSessions[0].step] || visitor.previousSessions[0].step || '?'}
          {visitor.previousSessions[0].trademarkName && visitor.previousSessions[0].trademarkName !== visitor.trademarkName
            ? ` (“${visitor.previousSessions[0].trademarkName}”)`
            : ''}
          {visitor.previousSessions.length > 1 ? ` · +${visitor.previousSessions.length - 1} earlier` : ''}
        </div>
      )}

      {/* Last contacted */}
      {visitor.lastContactedAt && (
        <div className="mkt-card-contacted">
          Contacted {getTimeAgo(visitor.lastContactedAt)}
          {visitor.outreachLog?.length > 0 && ` (${visitor.outreachLog.length}×)`}
        </div>
      )}

      {/* Source + time */}
      <div className="mkt-card-footer">
        {source && <span className="mkt-card-source">{source}</span>}
        <span className="mkt-card-time">{timeAgo}</span>
      </div>

      {/* Compose next email */}
      {outreachStage && (
        <div className="mkt-card-outreach-stage">
          <button
            className="mkt-card-send-btn"
            onClick={(e) => { e.stopPropagation(); setOutreachOpen(true); }}
            title={`Compose: ${outreachStage.label}`}
          >
            ✉️ Email {visitor.name ? visitor.name.split(' ')[0] : 'them'}
          </button>
        </div>
      )}

      {/* Quick status actions */}
      {!['paid', 'do_not_contact', 'not_serious'].includes(visitor.contactStatus) && (
        <div className="mkt-card-quick-actions">
          <button
            className="mkt-card-quick-btn mkt-quick-snooze"
            onClick={(e) => {
              e.stopPropagation();
              setReminder.mutate({ visitorId: visitor.id, days: 3 });
            }}
            title="Snooze — remind me again in 3 days"
          >
            ⏰ 3d
          </button>
          {visitor.contactStatus !== 'call' && (
            <button
              className="mkt-card-quick-btn mkt-quick-call"
              onClick={(e) => {
                e.stopPropagation();
                updateStatus.mutate({ visitorId: visitor.id, status: 'call' });
              }}
              title="Move to the Call list — Ali will phone them"
            >
              📞 → Call list
            </button>
          )}
          <button
            className="mkt-card-quick-btn mkt-quick-not-serious"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Mark as Not Serious? This will move them out of the active pipeline.')) {
                updateStatus.mutate({ visitorId: visitor.id, status: 'not_serious' });
              }
            }}
            title="Not Serious — just clicking around"
          >
            🚫 Not Serious
          </button>
          <button
            className="mkt-card-quick-btn mkt-quick-dnc"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Mark as Do Not Contact? They asked not to be messaged.')) {
                updateStatus.mutate({ visitorId: visitor.id, status: 'do_not_contact' });
              }
            }}
            title="Do Not Contact — asked not to be messaged"
          >
            ⛔ DNC
          </button>
        </div>
      )}

      {/* Action icons */}
      <div className="mkt-card-actions">
        <a
          href={clarityUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mkt-card-action"
          title="Watch Clarity session"
        >
          👁
        </a>
        {visitor.websiteUrl && (
          <a
            href={visitor.websiteUrl.startsWith('http') ? visitor.websiteUrl : `https://${visitor.websiteUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mkt-card-action"
            title="Visit website"
          >
            🌐
          </a>
        )}
      </div>

      {/* Outreach compose modal */}
      <OutreachModal
        visitor={visitor}
        isOpen={outreachOpen}
        onClose={() => setOutreachOpen(false)}
      />
    </div>
  );
}

function formatSource(source, medium) {
  if (!source || source === 'unknown') return null;
  if (source === 'google_ads') return 'Google Ads';
  if (source === 'google_organic') return 'Organic';
  if (source === 'direct') return 'Direct';
  if (medium === 'cpc') return `${source}/cpc`;
  return source;
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

export default VisitorCard;
